"use client";

import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fadeInUp, staggerContainer } from "@/lib/animations";
import { Calendar, Sparkles, Plus, Loader2, X } from "lucide-react";
import Button from "@/components/ui/Button";
import GlassCard from "@/components/ui/GlassCard";
import EventCalendarPro from "./EventCalendar";
import EventCard from "./EventCard";
import EventRequestForm from "./EventRequestForm";
import EventSuccessModal from "./EventSuccessModal";
import EventDetailsModal from "./EventDetailsModal";
import { useAPI } from "@/app/hooks";
import eventService, {
  Event,
  EventStatus,
  BackendEvent,
} from "@/app/services/eventService";

export default function Events() {
  const [activeTab, setActiveTab] = useState<"upcoming" | "plan">("upcoming");
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [eventDetailsOpen, setEventDetailsOpen] = useState(false);
  const [loadingEventDetails, setLoadingEventDetails] = useState(false);

  // Fetch events from API
  const {
    data: eventsData,
    loading: eventsLoading,
    error: eventsError,
    refetch: refetchEvents,
  } = useAPI<BackendEvent>(
    () =>
      eventService.getEventsForHook({
        page: 1,
        limit: 100,
        is_public: true,
        status: EventStatus.UPCOMING,
      }),
    [],
  );

  // Transform backend events to display format
  const transformedEvents = useMemo(() => {
    const events = eventsData?.rows || [];
    return events.map((backendEvent) =>
      eventService.transformBackendEvent(backendEvent),
    );
  }, [eventsData]);

  // Get upcoming events (future events only)
  const upcomingEvents = useMemo(() => {
    return transformedEvents.filter((event) => {
      const eventDate = new Date(event.event_date_start);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return eventDate >= today;
    });
  }, [transformedEvents]);

  // Handle form success
  const handleFormSuccess = () => {
    setFormModalOpen(false);
    setSuccessModalOpen(true);
  };

  // Handle event click from calendar
  const handleEventClick = async (event: Event) => {
    try {
      setLoadingEventDetails(true);
      // Fetch full event details
      const fullEventData = await eventService.getEventById(event.id);
      const transformedEvent =
        eventService.transformBackendEvent(fullEventData);
      setSelectedEvent(transformedEvent);
      setEventDetailsOpen(true);
    } catch (error) {
      console.error("Failed to load event details:", error);
      // Fallback to basic event data
      setSelectedEvent(event);
      setEventDetailsOpen(true);
    } finally {
      setLoadingEventDetails(false);
    }
  };

  return (
    <section
      id="events"
      className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-8 bg-card/40"
    >
      <div className="container mx-auto max-w-7xl">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {/* Section Header */}
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <h2 className="text-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 px-4">
              Events
            </h2>
            <p className="text-text text-base sm:text-lg max-w-2xl mx-auto mb-8 px-4">
              Discover upcoming events or plan your own
            </p>

            {/* Tab Toggle */}
            <div className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-1 p-1.5 sm:p-2 rounded-xl sm:rounded-2xl bg-card/80 backdrop-blur-xl border border-border shadow-sm">
              <button
                onClick={() => setActiveTab("upcoming")}
                className={`w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-2 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 ${
                  activeTab === "upcoming"
                    ? "bg-primary text-white shadow-lg"
                    : "text-text hover:text-heading hover:bg-card/50"
                }`}
              >
                Upcoming Events
              </button>
              <button
                onClick={() => setActiveTab("plan")}
                className={`w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-2 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 ${
                  activeTab === "plan"
                    ? "bg-primary text-white shadow-lg"
                    : "text-text hover:text-heading hover:bg-card/50"
                }`}
              >
                Plan Your Event
              </button>
            </div>
          </motion.div>

          {/* Content */}
          <AnimatePresence mode="wait">
            {/* Upcoming Events Tab */}
            {activeTab === "upcoming" && (
              <motion.div
                key="upcoming"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Loading State */}
                {eventsLoading && (
                  <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-10 h-10 text-primary animate-spin" />
                  </div>
                )}

                {/* Error State */}
                {eventsError && (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                      <X className="w-8 h-8 text-red-600" />
                    </div>
                    <p className="text-red-600 mb-4">{eventsError}</p>
                    <button
                      onClick={refetchEvents}
                      className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {/* Events Content */}
                {!eventsLoading && !eventsError && (
                  <>
                    {upcomingEvents.length > 0 ? (
                      <div className="space-y-8">
                        {/* Professional Calendar */}
                        <EventCalendarPro
                          events={upcomingEvents}
                          onEventClick={handleEventClick}
                        />
                      </div>
                    ) : (
                      <motion.div
                        variants={fadeInUp}
                        className="text-center py-12"
                      >
                        <Calendar className="w-16 h-16 text-primary mx-auto mb-4" />
                        <h3 className="text-heading text-xl font-bold mb-2">
                          No Upcoming Events
                        </h3>
                        <p className="text-text mb-6">
                          Check back soon for new events
                        </p>
                      </motion.div>
                    )}
                  </>
                )}
              </motion.div>
            )}

            {/* Plan Your Event Tab */}
            {activeTab === "plan" && (
              <motion.div
                key="plan"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="max-w-4xl mx-auto"
              >
                <GlassCard className="relative p-8 sm:p-12 lg:p-16 overflow-hidden">
                  {/* Animated Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent" />
                  <motion.div
                    className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(124,179,66,0.2),transparent_70%)]"
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.3, 0.6, 0.3],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />

                  {/* Content */}
                  <div className="relative z-10 text-center">
                    <motion.div
                      className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/10 mb-5 sm:mb-6"
                      animate={{ rotate: [0, 360] }}
                      transition={{
                        duration: 20,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    >
                      <Calendar className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
                    </motion.div>

                    <h3 className="text-heading text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 px-4">
                      Plan Your Event With Us
                    </h3>
                    <p className="text-text text-base sm:text-lg md:text-xl max-w-xl mx-auto mb-6 sm:mb-8 px-4 leading-relaxed">
                      From conferences to exhibitions, we'll help you create an
                      unforgettable experience
                    </p>

                    {/* Features Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto mb-8">
                      {[
                        "Professional Event Planning",
                        "State-of-the-art Facilities",
                        "Catering & AV Services",
                        "Flexible Scheduling",
                      ].map((feature, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 text-text text-sm bg-card/50 rounded-lg p-3"
                        >
                          <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA Button */}
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={() => {
                        console.log("Opening form modal...");
                        setFormModalOpen(true);
                      }}
                      className="gap-2"
                    >
                      <Plus className="w-5 h-5" />
                      Start Planning
                    </Button>
                  </div>
                </GlassCard>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Event Request Form Modal - Fixed z-index */}
      <AnimatePresence>
        {formModalOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
              onClick={() => {
                console.log("Backdrop clicked");
                setFormModalOpen(false);
              }}
            />

            {/* Modal */}
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-background rounded-2xl shadow-2xl border border-border pointer-events-auto"
              >
                <div className="sticky top-0 bg-background z-10 px-6 sm:px-8 pt-6 pb-4 border-b border-border flex items-center justify-between">
                  <h2 className="text-heading text-2xl font-bold">
                    Plan Your Event
                  </h2>
                  <button
                    onClick={() => {
                      console.log("Close button clicked");
                      setFormModalOpen(false);
                    }}
                    className="p-2 hover:bg-muted rounded-lg transition-colors"
                  >
                    <Plus className="w-5 h-5 text-text rotate-45" />
                  </button>
                </div>

                <div className="p-6 sm:p-8">
                  <EventRequestForm
                    onClose={() => setFormModalOpen(false)}
                    onSuccess={handleFormSuccess}
                  />
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Event Details Modal */}
      <EventDetailsModal
        event={selectedEvent}
        isOpen={eventDetailsOpen}
        onClose={() => {
          setEventDetailsOpen(false);
          setSelectedEvent(null);
          setActiveTab("upcoming");
        }}
      />

      {/* Success Modal */}
      <EventSuccessModal
        isOpen={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
      />

      {/* Loading overlay for event details */}
      {loadingEventDetails && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[10000] flex items-center justify-center">
          <Loader2 className="w-10 h-10 text-primary animate-spin" />
        </div>
      )}
    </section>
  );
}
